import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Gamepad2, ShieldCheck, Truck, Star, PawPrint, Sofa, Watch, Shirt, ShoppingBag, Baby, Dumbbell, Tv, Wrench, Car, Smartphone, Laptop, Gem } from "lucide-react";
import { useCategories, subUrl } from "../../components/store/Header";
import { api, img } from "../../lib/api";
import { useLang } from "../../lib/ctx";
import { ProductGrid, SectionHead } from "../../components/store/ProductCard";
import { Newsletter } from "../../components/store/Footer";
import { HeroBanner } from "../../components/store/HeroBanner";
import { PromoBanner } from "../../components/store/PromoBanner";
import { TopupTile } from "../../components/store/TopupBrand";
import { useSeo } from "../../lib/api";

const ICONS = { "pet-supplies": PawPrint, "home-garden-furniture": Sofa, "jewelry-watches": Watch, "mens-clothing": Shirt, "bags-shoes": ShoppingBag, "toys-kids-babies": Baby,
  "sports-outdoors": Dumbbell, "consumer-electronics": Tv, "home-improvement": Wrench, "automobiles-motorcycles": Car, "phones-accessories": Smartphone, "computer-office": Laptop };

const CategoryGrid = () => {
  const { t, lang } = useLang();
  const cats = useCategories();
  const tiles = [{ slug: "vintage", name: "Vintage", name_bm: "Vintaj", to: "/shop/vintage", Icon: Gem }, { slug: "topup", name: "Game Top-Up", name_bm: t("topup"), to: "/game-top-up", Icon: Gamepad2 },
    ...cats.map((c) => ({ ...c, to: subUrl(c.slug), Icon: ICONS[c.slug] || ShoppingBag }))];
  return (
    <section className="mt-4 bg-white sm:mx-8 sm:rounded lg:mx-12 xl:mx-auto xl:max-w-[74rem]" data-testid="category-grid">
      <p className="border-b border-[#efefef] px-5 py-4 text-sm font-semibold uppercase text-[#757575]">{lang === "bm" ? "Kategori" : "Categories"}</p>
      <div className="grid grid-cols-4 sm:grid-cols-7 lg:grid-cols-[repeat(14,minmax(0,1fr))]">
        {tiles.map(({ slug, name, name_bm, to, Icon }) => (
          <Link key={slug} to={to} className="group flex flex-col items-center gap-2 border-b border-r border-[#f3f3f3] px-1 py-4 text-center transition-shadow duration-200 hover:z-10 hover:shadow-[0_0_8px_rgba(0,0,0,.12)]" data-testid={`category-tile-${slug}`}>
            <span className="grid h-12 w-12 place-items-center rounded-full bg-[#fff1ee] text-[#ee4d2d] transition-transform duration-200 group-hover:scale-110"><Icon className="h-6 w-6" /></span>
            <span className="line-clamp-2 text-[11px] leading-tight text-[#222]">{lang === "bm" ? name_bm : name}</span>
          </Link>
        ))}
      </div>
    </section>
  );
};

const Trust = () => {
  const { lang } = useLang();
  const bm = lang === "bm";
  return (
    <div className="border-y border-white/10 bg-[#0d0d0d]" data-testid="trust-strip">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-4 px-4 py-5 text-xs uppercase tracking-widest text-zinc-400 sm:grid-cols-3 sm:px-8 lg:px-12">
        <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 shrink-0 text-[#C8A45D]" /> {bm ? "Label keadaan & ketulenan yang jujur" : "Honest condition & authenticity labels"}</span>
        <span className="flex items-center gap-2"><Truck className="h-4 w-4 shrink-0 text-[#C8A45D]" /> {bm ? "Penghantaran J&T, Pos Laju & SPX seluruh Malaysia" : "J&T, Pos Laju & SPX delivery across Malaysia"}</span>
        <span className="flex items-center gap-2"><Gamepad2 className="h-4 w-4 shrink-0 text-[#C8A45D]" /> {bm ? "Top-up game & kad hadiah selepas bayaran disahkan" : "Game & gift card top-ups after verified payment"}</span>
      </div>
    </div>
  );
};

const Block = ({ eyebrow, title, to, items, testid }) => {
  const { t } = useLang();
  if (!items?.length) return null;
  return (
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-12" data-testid={testid}>
      <SectionHead eyebrow={eyebrow} title={title} to={to} cta={t("viewAll")} />
      <ProductGrid items={items} />
    </section>
  );
};

const VintageVault = ({ items }) => {
  const { t, pick } = useLang();
  const hero = items?.[0];
  return (
    <section className="relative overflow-hidden bg-[#F4F4F4] text-[#080808]" data-testid="vintage-vault-section">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-24 sm:px-8 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-5">
          <p className="eyebrow">Exclusive · 1 of 1</p>
          <h2 className="mt-3 font-display text-6xl leading-none sm:text-7xl">{t("vintageVault")}</h2>
          <p className="mt-5 max-w-sm text-zinc-600">{t("vintageSub")}</p>
          <Link to="/shop/vintage" className="mt-8 inline-flex items-center gap-2 bg-[#080808] px-6 py-3 text-sm font-semibold uppercase tracking-widest text-white transition-colors duration-200 hover:bg-[#1B1B1B]" data-testid="vintage-vault-cta">{t("viewAll")} <ArrowRight className="h-4 w-4" /></Link>
        </div>
        {hero && (
          <Link to={`/product/${hero.slug}`} className="group relative lg:col-span-7" data-testid="vintage-vault-feature">
            <img src={img(hero.images?.[0]?.url)} alt={hero.title} className="aspect-[16/11] w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]" />
            <div className="absolute bottom-0 left-0 bg-[#080808] p-5 text-white">
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#C8A45D]">{hero.brand}</p>
              <p className="font-display text-2xl">{pick(hero, "title")}</p>
            </div>
          </Link>
        )}
      </div>
    </section>
  );
};

const TOP = ["mlbb", "pubg", "ff", "genshin", "hok", "roblox", "steam", "codm", "hsr", "valorant", "apple", "playstation"];
const rank = (s) => { const i = TOP.indexOf(s); return i < 0 ? 99 : i; };

const TopupPromo = () => {
  const { t, lang } = useLang();
  const [games, setGames] = useState([]);
  useEffect(() => {
    api.get("/games").then((r) => { setGames([...r.data].sort((a, b) => rank(a.slug) - rank(b.slug)).slice(0, 12)); }).catch(() => {});
  }, []);
  return (
    <section className="mx-auto max-w-7xl px-4 py-12 sm:px-8 lg:px-12" data-testid="topup-promo-section">
      <div className="keep-dark relative overflow-hidden rounded-sm">
        <div className="sp-header absolute inset-0" />
        <img src="/images/gaming2.jpg" alt="Mobile gaming" className="absolute inset-0 h-full w-full object-cover opacity-20 mix-blend-multiply" />
        <div className="relative flex flex-col gap-6 p-6 sm:p-10 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/90">Digital</p>
            <h2 className="mt-2 text-3xl font-extrabold sm:text-4xl">{t("topupTitle")}</h2>
            <p className="mt-3 text-sm text-white/90 sm:text-base">{t("topupSub")}</p>
          </div>
          <Link to="/game-top-up" className="inline-flex w-fit shrink-0 items-center gap-2 rounded-sm bg-white px-5 py-3 text-sm font-bold text-[#ee4d2d] transition-transform duration-200 hover:-translate-y-0.5" data-testid="topup-promo-btn">{t("topupCta")} <Gamepad2 className="h-4 w-4" /></Link>
        </div>
      </div>
      {games.length > 0 && (
        <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6" data-testid="topup-promo-games">
          {games.map((g) => (
            <Link key={g.slug} to={`/game-top-up?item=${g.slug}`} data-testid={`topup-promo-${g.slug}`}><TopupTile g={g} /></Link>
          ))}
        </div>
      )}
      {games.length > 0 && (
        <div className="mt-5 flex justify-center">
          <Link to="/game-top-up" className="inline-flex items-center gap-2 rounded-sm border border-[#ee4d2d] bg-white px-6 py-2.5 text-sm font-bold text-[#ee4d2d] transition-colors duration-200 hover:bg-[#ee4d2d] hover:text-white" data-testid="topup-promo-see-more">{lang === "bm" ? "Lihat semua game" : "See more games"}</Link>
        </div>
      )}
    </section>
  );
};

const SAMPLE_REVIEWS = [
  ["Aiman", 5, "Packaging was neat and the item matched the photos exactly."], ["Siti", 5, "Fast reply on WhatsApp and delivery reached Penang in 3 days."],
  ["Jason", 4, "Good quality for the price. Size chart was accurate."], ["Nurul", 5, "Game top-up credited quickly after payment."],
  ["Hafiz", 5, "Vintage shirt condition was honest, exactly as described."], ["Mei Ling", 4, "Easy checkout with FPX. Will buy again."],
  ["Arif", 5, "Tracking number came by email the next day."], ["Farah", 4, "Nice gift for my husband, he loved it."],
  ["Daniel", 5, "Price was cheaper than other shops I checked."], ["Aina", 5, "Customer service helped me change the size before shipping."],
].map(([name, rating, text], i) => ({ id: `sample-${i}`, name, rating, text, sample: true }));

const Reviews = ({ reviews }) => {
  const { t, lang } = useLang();
  const [more, setMore] = useState(false);
  const all = reviews.length ? reviews : SAMPLE_REVIEWS;
  const list = more ? all : all.slice(0, 2);
  const bm = lang === "bm";
  return (
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-12" data-testid="reviews-section">
      <SectionHead eyebrow="Community" title={t("reviews")} />
      {!reviews.length && <p className="mb-4 text-xs text-zinc-500" data-testid="sample-reviews-note">{bm ? "Contoh ulasan pelanggan" : "Sample customer reviews"}</p>}
      <div className="grid gap-4 sm:grid-cols-2">
        {list.map((r) => (
          <div key={r.id} className="card-dark p-5" data-testid={`review-card-${r.id}`}>
            <div className="flex gap-1 text-[#C8A45D]">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}</div>
            <p className="mt-3 text-sm text-zinc-300">“{r.text}”</p>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{r.name} · {r.sample ? (bm ? "Contoh" : "Sample") : "Verified purchase"}</p>
          </div>
        ))}
      </div>
      {all.length > 2 && (
        <button onClick={() => setMore(!more)} className="btn-ghost mt-6" data-testid="reviews-see-more-btn">
          {more ? (bm ? "Tunjuk kurang" : "Show less") : `${bm ? "Lihat lagi" : "See more"} (${all.length - 2})`}
        </button>
      )}
    </section>
  );
};

const News = ({ posts }) => {
  const { t, pick } = useLang();
  if (!posts.length) return null;
  return (
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-12" data-testid="news-section">
      <SectionHead eyebrow="Journal" title={t("news")} to="/blog" cta={t("viewAll")} />
      <div className="grid gap-8 md:grid-cols-2">
        {posts.slice(0, 2).map((p) => (
          <Link key={p.id} to={`/blog/${p.slug}`} className="group" data-testid={`news-card-${p.slug}`}>
            <img src={img(p.cover)} alt={p.title} className="aspect-[16/9] w-full object-cover transition-opacity duration-300 group-hover:opacity-80" />
            <p className="eyebrow mt-4">{p.category}</p>
            <h3 className="mt-2 font-display text-3xl group-hover:text-[#C8A45D]">{pick(p, "title")}</h3>
            <p className="mt-2 text-sm text-zinc-400">{p.excerpt}</p>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default function Home() {
  const { t } = useLang();
  const [d, setD] = useState({ jerseys: [], foot: [], vintage: [], newest: [], trending: [], best: [], reviews: [], posts: [], brands: [] });
  useSeo("", "DDVINTAGE CO — vintage finds, fashion, home, gadgets and digital top-ups for games and gift cards in Malaysia.", { "@context": "https://schema.org", "@type": "Organization", name: "DDVINTAGE CO", url: "https://ddvintage.co", email: "danishdaniyal159@gmail.com", telephone: "+60123655966" });
  useEffect(() => {
    const g = (p) => api.get(p).then((r) => r.data).catch(() => []);
    Promise.all([g("/products?category=sports-outdoors&sub=Sportswear&limit=4"), g("/products?category=sports-outdoors&sub=Sneakers&limit=2"), g("/products?category=bags-shoes&limit=2"), g("/products?category=vintage&limit=3"),
      g("/products?limit=4"), g("/products?tag=trending&limit=4"), g("/products?tag=bestseller&limit=4"), g("/reviews/latest"), g("/blog"), g("/catalog/meta")])
      .then(([j, b, s, v, n, tr, be, rv, po, meta]) => setD({ jerseys: j, foot: [...b, ...s], vintage: v, newest: n, trending: tr, best: be, reviews: rv, posts: po, brands: meta.brands || [] }));
  }, []);
  return (
    <div data-testid="home-page">
      <HeroBanner />
      <PromoBanner />
      <CategoryGrid />
      <Trust />
      <Block eyebrow="Matchday" title={t("featuredJerseys")} to="/shop/sports-outdoors?sub=Sportswear" items={d.jerseys} testid="featured-jerseys-section" />
      <Block eyebrow="Footwear" title={t("latestFootwear")} to="/shop/sports-outdoors?sub=Sneakers" items={d.foot} testid="footwear-section" />
      <VintageVault items={d.vintage} />
      <Block eyebrow="Just landed" title={t("newArrivals")} to="/new-arrivals" items={d.newest} testid="new-arrivals-section" />
      <Block eyebrow="Hot" title={t("trending")} to="/shop" items={d.trending} testid="trending-section" />
      <TopupPromo />
      <Block eyebrow="Proven" title={t("bestSellers")} to="/shop" items={d.best} testid="best-sellers-section" />
      <Reviews reviews={d.reviews} />
      <News posts={d.posts} />
      <Newsletter />
    </div>
  );
}
